import { useEffect, useMemo, useState, type FormEvent } from "react";
import { CheckCircle2, Clock3, Eye, EyeOff, LoaderCircle, LockKeyhole } from "lucide-react";
import { apiFetch } from "../lib/api";
import { translations, type Locale } from "../lib/i18n";
import { validatePassword, type PasswordChecks } from "../lib/passwordPolicy";
import type { ThemeMode } from "../lib/theme";
import { ThemeSwitcher } from "./ThemeSwitcher";

export function SetPassword({
  locale,
  themeMode,
  onThemeChange,
}: {
  locale: Locale;
  themeMode: ThemeMode;
  onThemeChange: (mode: ThemeMode) => void;
}) {
  const t = translations[locale];
  const token = useMemo(() => new URLSearchParams(window.location.search).get("token") || "", []);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(token ? null : t.callbackError);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [checkingToken, setCheckingToken] = useState(Boolean(token));
  const [tokenValid, setTokenValid] = useState(false);
  const passwordChecks = useMemo(() => validatePassword(password), [password]);

  const canSubmit = tokenValid && passwordChecks.valid && password === confirmPassword && confirmPassword.length > 0;

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    void apiFetch<{ valid: true; expiresAt: string }>("/api/auth/password/token-status", {
      method: "POST",
      body: JSON.stringify({ token }),
    })
      .then(() => {
        if (!cancelled) setTokenValid(true);
      })
      .catch(() => {
        if (!cancelled) setTokenValid(false);
      })
      .finally(() => {
        if (!cancelled) setCheckingToken(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError(t.passwordMismatch);
      return;
    }
    if (!passwordChecks.valid) {
      setError(t.passwordPolicyError);
      return;
    }
    setSubmitting(true);
    try {
      await apiFetch<{ ok: true }>("/api/auth/password/set", {
        method: "POST",
        body: JSON.stringify({ token, password }),
      });
      setSuccess(true);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t.callbackError);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="set-password-screen">
      <section className="set-password-panel">
        <div className="set-password-topbar">
          <a className="compact-brand" href="/">
            <img src="/assets/bgt-logo.png" alt="BGT" />
            <span>{t.productName}</span>
          </a>
          <ThemeSwitcher locale={locale} mode={themeMode} onChange={onThemeChange} />
        </div>
        {checkingToken ? (
          <div className="set-success set-password-status" role="status">
            <LoaderCircle className="status-spinner" />
            <h1>{t.checkingSession}</h1>
            <p>{t.checkingSessionText}</p>
          </div>
        ) : success ? (
          <div className="set-success">
            <CheckCircle2 />
            <h1>{t.setPasswordSuccess}</h1>
            <a className="primary-link" href="/">{t.retry}</a>
          </div>
        ) : !tokenValid ? (
          <div className="set-success set-password-status" role="alert">
            <Clock3 />
            <span className="form-eyebrow">{t.passwordShort}</span>
            <h1>{t.sessionExpired}</h1>
            <p>{t.sessionExpiredText}</p>
            <a className="primary-link" href="/">{t.requestAnotherLink}</a>
          </div>
        ) : (
          <form onSubmit={submit}>
            <span className="form-eyebrow">{t.passwordShort}</span>
            <h1>{t.setPasswordTitle}</h1>
            <p>{t.setPasswordText}</p>
            <label className="field-label" htmlFor="new-password">{t.newPassword}</label>
            <div className="input-shell">
              <LockKeyhole size={20} />
              <input id="new-password" type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} minLength={8} maxLength={128} autoComplete="new-password" aria-describedby="password-requirements" />
              <button type="button" className="password-toggle" onClick={() => setShowPassword((current) => !current)} aria-label={showPassword ? t.hidePassword : t.showPassword}>{showPassword ? <EyeOff size={19} /> : <Eye size={19} />}</button>
            </div>
            <PasswordRequirements checks={passwordChecks} t={t} />
            <label className="field-label" htmlFor="confirm-password">{t.confirmPassword}</label>
            <div className="input-shell">
              <LockKeyhole size={20} />
              <input id="confirm-password" type={showPassword ? "text" : "password"} value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} minLength={8} maxLength={128} autoComplete="new-password" />
            </div>
            {error ? <div className="form-error">{error}</div> : null}
            <button className="login-submit" type="submit" disabled={!canSubmit || submitting}>{submitting ? t.saving : t.setPasswordButton}</button>
          </form>
        )}
      </section>
    </main>
  );
}

function PasswordRequirements({
  checks,
  t,
}: {
  checks: PasswordChecks;
  t: typeof translations.sq | typeof translations.en;
}) {
  const requirements = [
    { key: "minLength", done: checks.minLength, label: t.passwordRuleMinLength },
    { key: "uppercase", done: checks.uppercase, label: t.passwordRuleUppercase },
    { key: "lowercase", done: checks.lowercase, label: t.passwordRuleLowercase },
    { key: "number", done: checks.number, label: t.passwordRuleNumber },
    { key: "symbol", done: checks.symbol, label: t.passwordRuleSymbol },
    { key: "noSpaces", done: checks.noSpaces, label: t.passwordRuleNoSpaces },
  ];

  return (
    <div className="password-requirements" id="password-requirements">
      <strong>{t.passwordRulesTitle}</strong>
      <ul>
        {requirements.map((requirement) => (
          <li key={requirement.key} className={requirement.done ? "met" : ""}>
            <span>{requirement.done ? "✓" : "•"}</span>
            {requirement.label}
          </li>
        ))}
      </ul>
      <small>{t.passwordSymbolsHint}</small>
    </div>
  );
}
